import { z } from "zod";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const LetterTypeSchema = z.enum(["sick_note", "school_note", "return_to_work", "general"]);
/** Content only: identity and the signature always come from the server. */
export const LetterContentSchema = z
    .object({
    type: LetterTypeSchema,
    addressee: z.string().trim().min(1).max(500).nullish(),
    bodyParagraphs: z.array(z.string().trim().min(1).max(4_000)).min(1).max(24),
    effectiveFrom: z.iso.date().nullish(),
    effectiveUntil: z.iso.date().nullish(),
    restrictions: z.array(z.string().trim().min(1).max(1_000)).max(20),
})
    .strict();
export const LetterEditsSchema = LetterContentSchema.partial().strict();
export const LetterDraftSchema = LetterContentSchema.extend({
    /** Explicit replacements; null clears an optional value, [] clears restrictions. */
    physicianEdits: LetterEditsSchema.optional(),
})
    .strict()
    .superRefine((draft, context) => {
    const content = { ...draft, ...draft.physicianEdits };
    if (content.effectiveFrom &&
        content.effectiveUntil &&
        content.effectiveFrom > content.effectiveUntil) {
        context.addIssue({
            code: "custom",
            path: ["effectiveUntil"],
            message: "The end date must be on or after the start date",
        });
    }
});
export const LetterRenderRequestSchema = z
    .object({
    siteId: IdSchema,
    patientId: IdSchema,
    expectedEncounterVersion: z.number().int().positive(),
    draft: LetterDraftSchema,
    mode: z.enum(["preview", "final"]),
    clinicianReviewed: z.boolean(),
    reviewContextHash: Sha256Schema.optional(),
    idempotencyKey: IdempotencyKeySchema.optional(),
    actionKey: z.string().max(1000).optional(),
    title: z.string().min(1).max(240).optional(),
})
    .strict()
    .superRefine((input, context) => {
    if (input.mode === "final" && !input.clinicianReviewed) {
        context.addIssue({
            code: "custom",
            path: ["clinicianReviewed"],
            message: "Review the letter before generating the signed PDF",
        });
    }
});
export const LetterRenderResponseSchema = z
    .object({
    draft: LetterDraftSchema,
    mode: z.enum(["preview", "final"]),
    fileName: z.string().min(1).max(240),
    mimeType: z.literal("application/pdf"),
    pdfBase64: z.string().min(1).max(5_000_000),
    contentHash: Sha256Schema,
    pageCount: z.number().int().positive().max(6),
    renderedAt: IsoDateTimeSchema,
    signatureVersionId: IdSchema.nullable(),
    documentId: IdSchema.optional(),
    reviewContextHash: Sha256Schema.optional(),
    template: z
        .object({ id: z.literal("emr-clinic-letter"), version: z.literal("2026-09") })
        .strict(),
})
    .strict();
export const DEFAULT_SICK_NOTE_WORDING = "Please excuse the patient from work for the dates indicated below due to illness.";
export const DEFAULT_SCHOOL_NOTE_WORDING = "Please excuse the patient from school for the dates indicated below due to illness.";
export function defaultLetterDraft(type = "sick_note") {
    return {
        type,
        addressee: null,
        bodyParagraphs: [
            type === "sick_note"
                ? DEFAULT_SICK_NOTE_WORDING
                : type === "school_note"
                    ? DEFAULT_SCHOOL_NOTE_WORDING
                    : "This letter is provided at the patient's request.",
        ],
        effectiveFrom: null,
        effectiveUntil: null,
        restrictions: [],
    };
}
/** Resolves only the explicit date phrase selected by the intent parser; unknown wording stays editable. */
export function letterDatesFromText(text, referenceDate) {
    const unknown = { effectiveFrom: null, effectiveUntil: null };
    if (!text)
        return unknown;
    const value = text
        .toLowerCase()
        .trim()
        .replace(/(\d)(st|nd|rd|th)\b/g, "$1")
        .replace(/[–—]/g, "-");
    const checked = (from, until = from) => z.iso.date().safeParse(from).success && z.iso.date().safeParse(until).success && from <= until
        ? { effectiveFrom: from, effectiveUntil: until }
        : unknown;
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})(?:\s*(?:to|through|and|-)\s*(\d{4}-\d{2}-\d{2}))?$/);
    if (iso?.[1])
        return checked(iso[1], iso[2]);
    if (referenceDate && z.iso.date().safeParse(referenceDate).success) {
        const relative = value.match(/^(yesterday|today|tomorrow)(?:\s*(?:and|to|through|-)\s*(yesterday|today|tomorrow))?$/);
        const day = (word) => new Date(Date.parse(`${referenceDate}T12:00:00Z`) +
            ({ yesterday: -1, today: 0, tomorrow: 1 }[word] ?? 0) * 86_400_000)
            .toISOString()
            .slice(0, 10);
        if (relative?.[1])
            return checked(day(relative[1]), day(relative[2] ?? relative[1]));
    }
    const months = [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
    ];
    const named = value.match(/^([a-z]+)\.?\s+(\d{1,2})(?:\s*(?:to|through|and|-)\s*(?:([a-z]+)\.?\s+)?(\d{1,2}))?(?:,?\s+(\d{4}))?$/);
    if (!named?.[1] || !named[2])
        return unknown;
    const month = (name) => name === "sept"
        ? 9
        : months.findIndex((item) => item === name || item.slice(0, 3) === name) + 1;
    const year = named[5] ?? referenceDate?.slice(0, 4);
    const firstMonth = month(named[1]);
    const lastMonth = named[3] ? month(named[3]) : firstMonth;
    if (!year || firstMonth === 0 || lastMonth === 0)
        return unknown;
    const date = (m, d) => `${year}-${String(m).padStart(2, "0")}-${d.padStart(2, "0")}`;
    return checked(date(firstMonth, named[2]), date(lastMonth, named[4] ?? named[2]));
}
export function resolveLetterContent(draft) {
    const parsed = LetterDraftSchema.parse(draft);
    const { physicianEdits, ...content } = parsed;
    return LetterContentSchema.parse({ ...content, ...physicianEdits });
}
//# sourceMappingURL=letters.js.map